import { useLocation, Link } from "react-router-dom"
import { ChevronRight, ChevronDown, PanelLeftClose, PanelLeftOpen, LayoutGrid } from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@shared/components/ui/sidebar"
import { useState, useEffect, memo, useCallback } from "react"
import { cn } from "@shared/lib/utils"
import type { NavItem, NavSubItem } from "@shared/types/navigation"
import { getNavigationConfig } from "@shared/lib/configs/navigation-config"
import { useAuthStore } from "@features/auth/hooks/useAuthStore"
import { getCurrentPartner, getPartnerLogo, getPartnerLogoHeight, getPartnerLogoAlt } from "@shared/utils/partner.utils"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/components/ui/tooltip"
import { Button } from "@shared/components/ui/button"
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@shared/components/ui/dropdown-menu"

// Constants
const SUBMENU_INDENT = "ml-2"

// Dev navigation (bottom utility area — not role-gated)
const DEV_NAV_ITEM = {
  title: "Dev",
  icon: LayoutGrid,
  url: "/dev",
  children: [
    { title: "Pages", url: "/dev/pages" },
    { title: "Dialogs", url: "/dev/dialogs" },
    { title: "Sheets", url: "/dev/sheets" },
    { title: "Components", url: "/dev/components" },
    { title: "Transactions", url: "/dev/transaction-components" },
    { title: "Opportunities", url: "/dev/opportunities-components" },
    { title: "Cost Analytics", url: "/dev/payments-cost-components" },
    { title: "Risk Scores", url: "/dev/risk-scores" },
    { title: "Rule Builder", url: "/dev/rule-builder" },
    { title: "Test", url: "/dev/test" },
  ],
} as NavItem & { children: NavSubItem[] }

// Style constants for collapsed/expanded states
const BUTTON_STYLES = {
  collapsed: {
    container: "w-8 mx-auto",
    content: "w-8 justify-center",
  },
  expanded: {
    container: "w-full",
    content: "w-full gap-2",
  }
} as const

// Utility functions for common styling patterns
const getButtonContainerClass = (isCollapsed: boolean) => 
  isCollapsed ? BUTTON_STYLES.collapsed.container : BUTTON_STYLES.expanded.container

const getButtonContentClass = (isCollapsed: boolean) => 
  cn("flex items-center", isCollapsed ? BUTTON_STYLES.collapsed.content : BUTTON_STYLES.expanded.content)

const getSidebarPadding = (isCollapsed: boolean) => 
  cn("px-5", isCollapsed && "px-2")

const getDropdownButtonClass = (isDropdownOpen: boolean, parentActive: boolean) => 
  cn(
    "w-8 mx-auto justify-center",
    !parentActive && isDropdownOpen && "bg-sidebar-accent text-sidebar-accent-foreground",
    parentActive && "bg-transparent text-primary font-medium hover:bg-transparent hover:text-primary",
    parentActive && isDropdownOpen && "bg-sidebar-accent text-primary font-medium hover:bg-sidebar-accent hover:text-primary"
  )

// Common layout pattern component for consistent header structure
const SidebarHeaderContent = memo(({ isCollapsed, currentPartner, toggleCollapse }: {
  isCollapsed: boolean
  currentPartner: string | null
  toggleCollapse: () => void
}) => (
  <div className={cn(
    "flex items-center",
    isCollapsed ? "justify-center" : "justify-between"
  )}>
    {!isCollapsed && (
      <img
        src={getPartnerLogo(currentPartner, false)}
        alt={getPartnerLogoAlt(currentPartner)}
        className={getPartnerLogoHeight(currentPartner)}
      />
    )}
    <Button
      variant="ghost"
      onClick={toggleCollapse}
      className={cn(
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md transition-colors",
        isCollapsed
          ? "h-6 w-6 p-0 justify-center mx-auto"
          : "ml-auto w-8 h-8 p-0"
      )}
    >
      {isCollapsed ? (
        <PanelLeftOpen className="h-4 w-4" />
      ) : (
        <PanelLeftClose className="h-4 w-4" />
      )}
    </Button>
  </div>
))
SidebarHeaderContent.displayName = "SidebarHeaderContent"

// Reusable tooltip wrapper component
interface TooltipWrapperProps {
  children: React.ReactNode
  tooltip: string
  isCollapsed: boolean
}

const TooltipWrapper = memo(({ children, tooltip, isCollapsed }: TooltipWrapperProps) => {
  if (!isCollapsed) {
    return <>{children}</>
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
})
TooltipWrapper.displayName = "TooltipWrapper"

function NavBadge({ text }: { text: string }) {
  return (
    <span className="ml-auto inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200 whitespace-nowrap">
      {text}
    </span>
  )
}

function SidebarNavLink({
  item,
  isActive,
}: {
  item: NavItem
  isActive: boolean
}) {
  const { isCollapsed } = useSidebar()

  const content = (
    <SidebarMenuButton asChild isActive={isActive} className={getButtonContainerClass(isCollapsed)}>
      <Link to={item.url} className={getButtonContentClass(isCollapsed)}>
        <item.icon className="h-4 w-4 shrink-0" />
        {!isCollapsed && <span className="truncate">{item.title}</span>}
        {!isCollapsed && item.badge && <NavBadge text={item.badge} />}
      </Link>
    </SidebarMenuButton>
  )

  return (
    <SidebarMenuItem>
      <TooltipWrapper tooltip={item.badge ? `${item.title} (${item.badge})` : item.title} isCollapsed={isCollapsed}>
        {content}
      </TooltipWrapper>
    </SidebarMenuItem>
  )
}

function SidebarCollapsibleMenu({
  item,
  pathname,
}: {
  item: NavItem & { children: NavSubItem[] }
  pathname: string
}) {
  const { isCollapsed } = useSidebar()
  const [isOpen, setIsOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  
  const isChildPathActive = useCallback((child: NavSubItem) =>
    pathname === child.url ||
    pathname.startsWith(child.url + "/") ||
    child.matchPaths?.some(p => pathname === p || pathname.startsWith(p + "/")) === true,
    [pathname]
  )

  const isParentActive = useCallback(() =>
    item.children?.some(child => isChildPathActive(child)),
    [item.children, isChildPathActive]
  )
  
  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), [])
  
  useEffect(() => {
    if (isParentActive()) {
      setIsOpen(true)
    }
  }, [pathname, isParentActive])

  if (isCollapsed) {
    const parentActive = isParentActive()
    
    return (
      <SidebarMenuItem>
        <DropdownMenu onOpenChange={setIsDropdownOpen}>
          <TooltipWrapper tooltip={item.title} isCollapsed={true}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton className={getDropdownButtonClass(isDropdownOpen, parentActive)}>
                <item.icon className="h-4 w-4 shrink-0" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
          </TooltipWrapper>
          <DropdownMenuContent side="right" sideOffset={8}>
            {item.children?.map((child) => (
              <DropdownMenuItem key={child.title} asChild>
                <Link to={child.url}>
                  {child.title}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={toggleOpen}>
        <item.icon className="h-4 w-4 shrink-0" />
        <span className="truncate flex-1 text-left">{item.title}</span>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
      </SidebarMenuButton>
      {isOpen && (
        <div className={cn(SUBMENU_INDENT, "mt-1 space-y-0 border-l border-border pl-3")}>
          {item.children?.map(child => {
            const isChildActive = isChildPathActive(child)
            return (
              <Link
                key={child.title}
                to={child.url}
                className={cn(
                  "flex items-center px-2 py-1.5 text-sm rounded transition-colors duration-150 hover:bg-muted",
                  isChildActive
                    ? "text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <span className="truncate">{child.title}</span>
                {child.badge && <NavBadge text={child.badge} />}
              </Link>
            )
          })}
        </div>
      )}
    </SidebarMenuItem>
  )
}

export function AppSidebar() {
  const { pathname } = useLocation()
  const { isCollapsed, toggleCollapse } = useSidebar()
  const currentPartner = getCurrentPartner()
  const role = useAuthStore((s) => s.role)
  // A logged-in merchant always sees their full left navigation. Service
  // enablement is surfaced informationally (the Products menu and the services
  // page) but must never hide a merchant's own nav items — gating the sidebar by
  // a mutable, shared enabledServices value repeatedly dropped POS, Engagement, etc.
  const navConfig = getNavigationConfig(role)

  function renderNavItem(item: NavItem) {
    if (item.children?.length) {
      return (
        <SidebarCollapsibleMenu
          key={item.url}
          item={item as NavItem & { children: NavSubItem[] }}
          pathname={pathname}
        />
      )
    }
    return (
      <SidebarNavLink
        key={item.url}
        item={item}
        isActive={pathname === item.url || (item.url !== '/' && pathname.startsWith(item.url + "/"))}
      />
    )
  }

  return (
    <TooltipProvider>
      <Sidebar>
        <SidebarHeader className={cn("pb-3", getSidebarPadding(isCollapsed))}>
          <SidebarHeaderContent
            isCollapsed={isCollapsed}
            currentPartner={currentPartner}
            toggleCollapse={toggleCollapse}
          />
        </SidebarHeader>

        <SidebarContent className={getSidebarPadding(isCollapsed)}>
          <SidebarGroup>
            {!isCollapsed && (
              <SidebarGroupLabel className="mb-1">
                Platform
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {navConfig.main.map(item => renderNavItem(item))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            {!isCollapsed && (
              <SidebarGroupLabel className="mb-1">
                {navConfig.servicesLabel}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {navConfig.services.map(item => renderNavItem(item))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <div className="mt-auto pt-4 pb-2">
            <SidebarMenu>
              <SidebarCollapsibleMenu
                item={DEV_NAV_ITEM}
                pathname={pathname}
              />

            </SidebarMenu>

            {/* Role indicator badge */}
            {isCollapsed ? (
              <div className="mt-2 flex justify-center">
                <TooltipWrapper tooltip={role === "bank" ? "Bank" : "Merchant"} isCollapsed={true}>
                  <span className={cn(
                    "inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold uppercase",
                    role === "bank"
                      ? "bg-blue-100 text-blue-700 border border-blue-200"
                      : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                  )}>
                    {role === "bank" ? "B" : "M"}
                  </span>
                </TooltipWrapper>
              </div>
            ) : (
              <div className="mt-2 px-2">
                <span className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium uppercase tracking-wide",
                  role === "bank"
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                )}>
                  <span className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    role === "bank" ? "bg-blue-500" : "bg-emerald-500"
                  )} />
                  {role}
                </span>
              </div>
            )}
          </div>
        </SidebarContent>
      </Sidebar>
    </TooltipProvider>
  )
}
